import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { ActivityIndicator, ScrollView, SxProp, useDripsyTheme, View } from 'dripsy';
import _ from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { DigitalDepositStackParamList } from './type';
import { Blockchain, DigitalAsset, DigitalDepositAddress } from '../../../../../../packages/utx-access-client/src/react';
import { Icon } from '@/design-system/components/icons/Icon';
import { useDepositAddresses } from '../details/hooks/useAccount.hook';
import QRCode from 'qrcode';
import { Divider } from '@/design-system/components/Divider';
import { ChainSelector } from '../../components/chainSelector/ChainSelector';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';

export interface DigitalDepositProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  account: string;
  refresh?: () => void;
  record?: DigitalAsset & { assetChains: string[]; chains?: Blockchain[] };
  onChangeView?: () => void;
}

export const DigitalDepositWebAddressScreen: React.FC<DigitalDepositProps> = (props) => {
  const { onClose, onLayout, account, refresh, record, onChangeView } = props;
  const { t } = useTranslation('digital-injection');
  const { theme } = useDripsyTheme();
  const layout = useContext(LayoutContext);
  const { body, textMb, btn } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<DigitalDepositStackParamList>>();

  const { queryDepositAddresses } = useDepositAddresses();
  const [chainsDisplay, setChainsDisplay] = useState<{ chain: string; chainName: string | undefined }[]>([]);
  const [selectedChain, setSelectedChain] = useState<string | undefined>(undefined);
  const [address, setAddress] = useState<(DigitalDepositAddress & { chainName: string | undefined }) | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const qrcodeRef = useRef(null);
  useEffect(() => {
    if (!selectedChain) return;
    queryDepositAddresses({ asset: record?.symbol, chain: selectedChain, accountId: [account] })
      .then((response) => {
        if (response.data && response.data.addresses) {
          let addresses = response.data.addresses;
          let addressesDisplayFlag: (DigitalDepositAddress & { chainName: string | undefined })[] = [];
          addresses?.forEach((a) => {
            let chain = record?.chains?.find((c) => c?.chain == a?.chain);
            if (chain) {
              addressesDisplayFlag.push({ ...a, chainName: chain?.name });
            }
          });
          addressesDisplayFlag = addressesDisplayFlag.sort(
            (a, b) => Number(a?.chainName?.charCodeAt(0)) - Number(b?.chainName?.charCodeAt(0))
          );
          setAddress(addressesDisplayFlag[0]);
        } else {
          setAddress(undefined);
        }
      })
      .catch((error) => {
        setAddress(undefined);
      });
  }, [selectedChain]);

  useEffect(() => {
    let chainsDisplayFlag: { chain: string; chainName: string | undefined }[] = [];
    record?.assetChains.forEach((ac) => {
      let chain = record?.chains?.find((c) => c?.chain == ac);
      if (chain) {
        chainsDisplayFlag.push({ chain: ac, chainName: chain?.name });
      }
    });
    if (chainsDisplayFlag.length) {
      setSelectedChain(chainsDisplayFlag[0].chain);
    }
    setChainsDisplay(chainsDisplayFlag);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!address) return;
    QRCode.toCanvas(
      qrcodeRef.current,
      address?.address || '',
      {
        width: 120,
        margin: 0,
        errorCorrectionLevel: 'H',
        color: { light: '#ffffff', dark: '#000000' }
      },
      (error) => {
        if (error) console.log(error);
      }
    );
  }, [address]);

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader
        title={t('digital-injection::injection-instruction.title--injection-instruction', { asset: record?.name })}
        closeModal={() => {
          navigation?.getParent()?.goBack();
          onChangeView && onChangeView();
        }}
      />
      <ScrollView sx={{ flex: 1, backgroundColor: 'transparent' }} nestedScrollEnabled>
        <View sx={body}>
          <TextTitle sx={textMb} semiBold>
            {t('digital-injection::injection-instruction.text--select-a-network', { asset: record?.name })}
          </TextTitle>
          <View sx={{ gap: '$1', zIndex: 99 }}>
            <TextBody>{t('digital-injection::glossary.network')}</TextBody>
            <ChainSelector
              value={address?.chain}
              chainsDisplay={chainsDisplay}
              onValueChange={() => {}}
              onChainChange={(value) => {
                value && setSelectedChain(value.chain);
              }}
            />
            {!isLoading ? (
              !chainsDisplay.length && (
                <TextCaption danger sx={{ mt: '$1' }}>
                  {t('digital-injection::glossary.no-network-available-error')}
                </TextCaption>
              )
            ) : (
              <View sx={{ padding: '$5' }}>
                <ActivityIndicator size="large" color={(theme.colors?.$disable as string) || '#F0F0F0'} />
              </View>
            )}
          </View>
          {address && (
            <>
              <View sx={{ alignItems: 'center' }}>
                <View sx={{ display: 'inline-block', position: 'relative' }}>
                  <canvas ref={qrcodeRef}></canvas>
                </View>
              </View>
              <View sx={{ gap: '$1' }}>
                <TextBody>{t('digital-injection::glossary.deposit-address')}</TextBody>
                <View
                  sx={{
                    gap: '$2',
                    flexDirection: 'row',
                    backgroundColor: '#f5f5f5',
                    height: '$9',
                    lineHeight: '$7',
                    alignItems: 'center',
                    borderRadius: '$radius1',
                    justifyContent: 'space-between'
                  }}>
                  <TextCaption sx={{ pl: '$2', overflow: 'hidden', height: '$9', lineHeight: '$7' }}>{address?.address}</TextCaption>
                  <UniIconButton
                    icon="copyWeb"
                    onPress={() => {
                      navigator.clipboard.writeText(address?.address || '');
                    }}
                    sx={{ pr: '$2' }}
                  />
                </View>
              </View>
              <View>
                <View sx={{ flexDirection: 'row', gap: '$1' }}>
                  <Icon name="note" />
                  <TextBody bold>{t('digital-injection::injection-instruction.text--important')}</TextBody>
                </View>
                <TextCaption>
                  {t('digital-injection::injection-instruction.text--accepted-address-one')} <TextBody semiBold>{record?.name}s</TextBody>{' '}
                  {t('digital-injection::injection-instruction.text--accepted-address-two')}{' '}
                  <TextBody semiBold>{address?.chainName}</TextBody>{' '}
                  {t('digital-injection::injection-instruction.text--accepted-address-three')}
                </TextCaption>
              </View>
            </>
          )}
          <Divider sx={{ margin: 'auto', backgroundColor: '#f0f0f0' }} />
          <View sx={{ gap: '$1' }}>
            <TextBody bold>{t('digital-injection::injection-instruction.text--notice')}</TextBody>
            <View sx={{ flexDirection: 'row', gap: '$1' }}>
              <TextCaption>1.</TextCaption>
              <TextCaption>{t('digital-injection::injection-instruction.text--notice-one')}</TextCaption>
            </View>
            <View sx={{ flexDirection: 'row', gap: '$1' }}>
              <TextCaption>2.</TextCaption>
              <TextCaption>{t('digital-injection::injection-instruction.text--notice-two')}</TextCaption>
            </View>
          </View>
          <UniButton
            sx={btn}
            variant={ButtonVariant.outline1}
            label={t('digital-injection::glossary.back')}
            onPress={() => {
              onClose();
              onChangeView && onChangeView();
            }}
          />
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28,
    gap: '$5'
  },
  textMb: {},
  btn: {
    width: '100%',
    height: 44
  }
};
